import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'

interface SalesChannel {
  id: number
  slug: string
  label: string
  url: string | null
  active: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export function SetariCanaleVanzarePage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('settings.sales_channels:write')

  const [editTarget, setEditTarget] = useState<SalesChannel | null>(null)
  const [createOpen, setCreateOpen] = useState(false)

  const query = useQuery<{ channels: SalesChannel[] }>({
    queryKey: ['sales-channels'],
    queryFn: async () =>
      (await api.get<{ channels: SalesChannel[] }>('/settings/sales-channels')).data,
  })

  const deleteMutation = useMutation({
    mutationFn: async ({ id, force }: { id: number; force?: boolean }) =>
      (await api.delete(`/settings/sales-channels/${id}${force ? '?force=true' : ''}`)).data,
    onSuccess: () => {
      toast.success('Canal șters')
      queryClient.invalidateQueries({ queryKey: ['sales-channels'] })
    },
    onError: showError,
  })

  const channels = query.data?.channels ?? []

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Setări → Canale vânzare</h1>
        {canWrite && <Button onClick={() => setCreateOpen(true)}>+ Canal nou</Button>}
      </div>

      <Card description="Canalele de vânzare apar ca opțiuni în Listări canale, Matrice prețuri B2C și Campanii promo. Codul intern (slug) e folosit de sistem (ex: „drmax”); denumirea e cea afișată în interfață („Dr. Max”).">
        {query.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă...</p>}
        {channels.length === 0 && !query.isLoading && (
          <p className="text-sm text-b4l-text-muted">
            Niciun canal definit. Apasă {'"Canal nou"'} pentru a începe.
          </p>
        )}
        {channels.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs font-semibold text-b4l-text-dim">
                  <th className="px-2 py-2">Ordine</th>
                  <th className="px-2 py-2">Cod intern</th>
                  <th className="px-2 py-2">Denumire afișată</th>
                  <th className="px-2 py-2">URL</th>
                  <th className="px-2 py-2">Stare</th>
                  {canWrite && <th className="px-2 py-2">Acțiuni</th>}
                </tr>
              </thead>
              <tbody>
                {channels.map((c) => (
                  <tr key={c.id} className="border-b border-b4l-border hover:bg-b4l-bg-soft">
                    <td className="px-2 py-1.5 text-xs">{c.sortOrder}</td>
                    <td className="px-2 py-1.5 font-mono text-xs">{c.slug}</td>
                    <td className="px-2 py-1.5 font-medium">{c.label}</td>
                    <td className="px-2 py-1.5 text-xs">
                      {c.url ? (
                        <a
                          href={c.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-b4l-blue-dark hover:underline"
                        >
                          {c.url.replace(/^https?:\/\//, '')}
                        </a>
                      ) : (
                        <span className="text-b4l-text-muted">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      <span
                        className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${
                          c.active
                            ? 'bg-b4l-green-light text-b4l-green-dark'
                            : 'bg-b4l-bg text-b4l-text-muted'
                        }`}
                      >
                        {c.active ? 'Activ' : 'Inactiv'}
                      </span>
                    </td>
                    {canWrite && (
                      <td className="px-2 py-1.5">
                        <div className="flex gap-1">
                          <Button variant="secondary" onClick={() => setEditTarget(c)}>
                            Editează
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Șterge canalul "${c.label}"?`)) {
                                deleteMutation.mutate({ id: c.id })
                              }
                            }}
                          >
                            Șterge
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {createOpen && <ChannelFormModal onClose={() => setCreateOpen(false)} />}
      {editTarget && <ChannelFormModal channel={editTarget} onClose={() => setEditTarget(null)} />}
    </div>
  )
}

function ChannelFormModal({ channel, onClose }: { channel?: SalesChannel; onClose: () => void }) {
  const queryClient = useQueryClient()
  const isEdit = !!channel

  const [slug, setSlug] = useState(channel?.slug ?? '')
  const [label, setLabel] = useState(channel?.label ?? '')
  const [url, setUrl] = useState(channel?.url ?? '')
  const [active, setActive] = useState(channel?.active ?? true)
  const [sortOrder, setSortOrder] = useState(channel?.sortOrder ?? 0)

  // Auto-generează slug din label la creare (dacă nu a fost editat manual)
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false)
  useEffect(() => {
    if (!isEdit && !slugManuallyEdited) {
      const auto = label
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
      setSlug(auto)
    }
  }, [label, isEdit, slugManuallyEdited])

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        slug: slug.trim(),
        label: label.trim(),
        url: url.trim() || null,
        active,
        sortOrder,
      }
      if (isEdit) {
        return (await api.patch(`/settings/sales-channels/${channel!.id}`, body)).data
      }
      return (await api.post('/settings/sales-channels', body)).data
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Canal actualizat' : 'Canal adăugat')
      queryClient.invalidateQueries({ queryKey: ['sales-channels'] })
      onClose()
    },
    onError: showError,
  })

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={isEdit ? `Editează canal: ${channel?.label}` : 'Canal nou'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={!slug.trim() || !label.trim()}
          >
            {isEdit ? 'Salvează' : 'Adaugă'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field
          label="Denumire afișată"
          placeholder="ex: Dr. Max"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <div>
          <label className="block text-xs font-medium text-b4l-text-dim">
            Cod intern (slug) — doar litere mici, cifre și cratimă
          </label>
          <input
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value)
              setSlugManuallyEdited(true)
            }}
            placeholder="ex: drmax"
            className="mt-1 w-full rounded border border-b4l-border bg-white px-2 py-1.5 font-mono text-sm"
            disabled={isEdit}
          />
          {isEdit && (
            <p className="mt-1 text-[10px] text-b4l-text-muted">
              Slug-ul nu poate fi schimbat după creare (referențe în alte tabele)
            </p>
          )}
        </div>
        <Field
          label="URL (opțional)"
          placeholder="ex: https://drmax.ro"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <Field
          label="Ordine afișare"
          type="number"
          value={String(sortOrder)}
          onChange={(e) => setSortOrder(Number(e.target.value))}
        />
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          <span>Activ (apare în dropdown-uri)</span>
        </label>
      </div>
    </Modal>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
